import React, { useState, useEffect, useMemo, useCallback } from "react";
import { projectTaskAPI } from '../../services/api';
import {
  Box, Card, CardBody, CardHeader, Heading, VStack, HStack, FormControl,
  FormLabel, Textarea, Button, Table, Thead, Tbody, Tr, Th, Td,
  Input, Select, Alert, AlertIcon, AlertDescription, useToast,
  SimpleGrid, Badge, Stat, StatLabel, StatNumber, Progress, Text, Flex, Icon,
  useColorModeValue, Divider
} from "@chakra-ui/react";
import {
  FiCalendar,
  FiTrendingUp,
  FiClock,
  FiTarget,
  FiActivity,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const WeeklyProgressForm = ({
  taskId,
  taskDetails,
  existingProgress,
  onSave,
  onCancel,
  autoCloseOnSave = false,
}) => {
  const toast = useToast();
  const currentDate = new Date();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isLoadingMonthData, setIsLoadingMonthData] = useState(false);

  // Default weekly data structure
  const defaultWeeklyData = useMemo(() => [
    { week: 1, targetedQty: "", achievedQty: "", consumedManhours: "" },
    { week: 2, targetedQty: "", achievedQty: "", consumedManhours: "" },
    { week: 3, targetedQty: "", achievedQty: "", consumedManhours: "" },
    { week: 4, targetedQty: "", achievedQty: "", consumedManhours: "" },
  ], []);

  const [formData, setFormData] = useState(() => ({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    weeklyData: [...defaultWeeklyData],
    additionalLapsedManhours: 0,
    justification: "",
  }));

  // Helper function to safely parse number from string (allows decimal entry)
  const safeParseNumber = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  // FIXED: Function to load month-specific data using existing API
  const loadMonthData = useCallback(async (year, month) => {
    if (!taskId) return;

    setIsLoadingMonthData(true);
    try {
      const response = await projectTaskAPI.getTaskProgressHistory(taskId);

      if (response.data?.success && response.data?.data) {
        const allProgress = response.data.data;
        const monthProgress = allProgress.find(p =>
          parseInt(p.year) === parseInt(year) &&
          parseInt(p.month) === parseInt(month)
        );

        if (monthProgress) {
          let weeklyData = [...defaultWeeklyData];

          if (monthProgress.weeklyBreakdown) {
            let parsedData;
            try {
              let breakdown = monthProgress.weeklyBreakdown;
              if (typeof breakdown === 'string') {
                breakdown = JSON.parse(breakdown);
                if (typeof breakdown === 'string') {
                  breakdown = JSON.parse(breakdown);
                }
              }
              parsedData = breakdown;
            } catch (parseError) {
              console.error("❌ JSON Parse Error:", parseError);
              parsedData = [];
            }

            if (Array.isArray(parsedData) && parsedData.length > 0) {
              weeklyData = parsedData.map((week, index) => ({
                week: index + 1,
                targetedQty: week.targetedQty?.toString() || "",
                achievedQty: week.achievedQty?.toString() || "",
                consumedManhours: week.consumedManhours?.toString() || ""
              }));

              while (weeklyData.length < 4) {
                weeklyData.push({
                  week: weeklyData.length + 1,
                  targetedQty: "",
                  achievedQty: "",
                  consumedManhours: ""
                });
              }
              weeklyData = weeklyData.slice(0, 4);
            }
          }

          setFormData(prev => ({
            ...prev,
            weeklyData: weeklyData,
            additionalLapsedManhours: Number(monthProgress.additionalLapsedManhours) || 0,
            justification: monthProgress.justification || ""
          }));

        } else {
          setFormData(prev => ({
            ...prev,
            weeklyData: [...defaultWeeklyData],
            additionalLapsedManhours: 0,
            justification: ""
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          weeklyData: [...defaultWeeklyData],
          additionalLapsedManhours: 0,
          justification: ""
        }));
      }
    } catch (error) {
      console.error("❌ Error loading month data:", error);
      setFormData(prev => ({
        ...prev,
        weeklyData: [...defaultWeeklyData],
        additionalLapsedManhours: 0,
        justification: ""
      }));
    } finally {
      setIsLoadingMonthData(false);
    }
  }, [taskId, defaultWeeklyData]);

  // Handle month/year changes
  const handlePeriodChange = useCallback((field, value) => {
    const numValue = parseInt(value);
    setFormData(prev => {
      const newFormData = { ...prev, [field]: numValue };
      if (field === 'month' || field === 'year') {
        const year = field === 'year' ? numValue : prev.year;
        const month = field === 'month' ? numValue : prev.month;
        loadMonthData(year, month);
      }
      return newFormData;
    });
  }, [loadMonthData]);

  // Initialize form data from existing progress
  useEffect(() => {
    if (existingProgress && !initialized) {
      try {
        let weeklyData = [...defaultWeeklyData];

        if (existingProgress.weeklyBreakdown) {
          let parsedData;
          try {
            let breakdown = existingProgress.weeklyBreakdown;
            if (typeof breakdown === 'string') {
              breakdown = JSON.parse(breakdown);
              if (typeof breakdown === 'string') {
                breakdown = JSON.parse(breakdown);
              }
            }
            parsedData = breakdown;
          } catch (parseError) {
            console.error("❌ JSON Parse Error:", parseError);
            parsedData = [];
          }

          if (Array.isArray(parsedData) && parsedData.length > 0) {
            weeklyData = parsedData.map((week, index) => ({
              week: index + 1,
              targetedQty: week.targetedQty?.toString() || "",
              achievedQty: week.achievedQty?.toString() || "",
              consumedManhours: week.consumedManhours?.toString() || ""
            }));

            while (weeklyData.length < 4) {
              weeklyData.push({
                week: weeklyData.length + 1,
                targetedQty: "",
                achievedQty: "",
                consumedManhours: ""
              });
            }
            weeklyData = weeklyData.slice(0, 4);
          }
        }

        setFormData({
          year: Number(existingProgress.year) || currentDate.getFullYear(),
          month: Number(existingProgress.month) || (currentDate.getMonth() + 1),
          weeklyData: weeklyData,
          additionalLapsedManhours: Number(existingProgress.additionalLapsedManhours) || 0,
          justification: existingProgress.justification || ""
        });

        setInitialized(true);
      } catch (error) {
        console.error('❌ Error processing existing progress:', error);
        toast({
          title: "Warning",
          description: "There was an issue loading the existing progress data",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } else if (!existingProgress) {
      setInitialized(true);
    }
  }, [existingProgress, initialized, defaultWeeklyData, currentDate, toast]);

  // Reset initialization when existingProgress changes
  useEffect(() => {
    setInitialized(false);
  }, [existingProgress]);

  // Safe calculation with array check
  const calculateTotals = useCallback(() => {
    const safeWeeklyData = Array.isArray(formData.weeklyData) ? formData.weeklyData : defaultWeeklyData;

    return safeWeeklyData.reduce(
      (acc, week) => ({
        totalTargeted: acc.totalTargeted + safeParseNumber(week.targetedQty),
        totalAchieved: acc.totalAchieved + safeParseNumber(week.achievedQty),
        totalConsumed: acc.totalConsumed + safeParseNumber(week.consumedManhours),
      }),
      { totalTargeted: 0, totalAchieved: 0, totalConsumed: 0 }
    );
  }, [formData.weeklyData, defaultWeeklyData]);

  // Calculate variances
  const calculateVariances = useCallback(() => {
    const totals = calculateTotals();
    const varianceQuantity = totals.totalAchieved - totals.totalTargeted;
    const expectedManhours = totals.totalAchieved * (taskDetails?.productivity || 0);
    const varianceManhours = totals.totalConsumed - expectedManhours;
    return { varianceQuantity, varianceManhours };
  }, [calculateTotals, taskDetails?.productivity]);

  // AUTO-CALCULATE Additional Lapsed Manhours
  const calculatedAdditionalLapsedManhours = useMemo(() => {
    const totals = calculateTotals();
    const expectedManhours = totals.totalAchieved * (taskDetails?.productivity || 0);
    const variance = totals.totalConsumed - expectedManhours;
    return Math.max(0, variance);
  }, [calculateTotals, taskDetails?.productivity]);

  // Update Additional Lapsed Manhours automatically
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      additionalLapsedManhours: calculatedAdditionalLapsedManhours
    }));
  }, [calculatedAdditionalLapsedManhours]);

  // FIXED: Handle weekly data change with string values to support decimal entry
  const handleWeeklyDataChange = useCallback((weekIndex, field, value) => {
    // Allow empty string, numbers, and decimal points during typing
    const regex = /^\d*\.?\d*$/;
    if (value === "" || regex.test(value)) {
      setFormData(prev => ({
        ...prev,
        weeklyData: prev.weeklyData.map((week, index) =>
          index === weekIndex
            ? { ...week, [field]: value }
            : week
        )
      }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert string values to numbers before submission
      const submissionData = {
        ...formData,
        weeklyData: formData.weeklyData.map(week => ({
          week: week.week,
          targetedQty: safeParseNumber(week.targetedQty),
          achievedQty: safeParseNumber(week.achievedQty),
          consumedManhours: safeParseNumber(week.consumedManhours)
        }))
      };

      const response = await projectTaskAPI.recordWeeklyProgress(taskId, submissionData);

      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message || "Weekly progress saved successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onSave && onSave(response.data.data);

        // if (autoCloseOnSave) {
        //   onCancel && onCancel();
        // }
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to save progress",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("❌ Error saving weekly progress:", error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please check your connection.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ FIXED: Calculate totals FIRST before using them
  const totals = calculateTotals();
  const variances = calculateVariances();

  // ✅ THEN calculate efficiency metrics using totals
  const progressEfficiency = useMemo(() => {
    if (totals.totalTargeted === 0) return 0;
    return (totals.totalAchieved / totals.totalTargeted) * 100;
  }, [totals.totalTargeted, totals.totalAchieved]);

  const productivityEfficiency = useMemo(() => {
    const expectedManhours = totals.totalAchieved * (taskDetails?.productivity || 0);
    if (expectedManhours === 0) return 100;
    return (expectedManhours / totals.totalConsumed) * 100;
  }, [totals.totalAchieved, totals.totalConsumed, taskDetails?.productivity]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  return (
    <VStack spacing={6} align="stretch" w="100%">
      <Card
        bg="#1a1d23"
        borderRadius="lg"
        border="1px solid"
        borderColor="#2d3139"
        shadow="lg"
      >
        <CardBody p={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">

              {/* Period Selection */}
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={FiCalendar} boxSize={5} color="#6b7280" mr={2} />
                  <Heading size="md" color="#e5e7eb" fontWeight="600">Reporting Period</Heading>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl>
                    <FormLabel color="#9ca3af" fontSize="sm" mb={2}>Year</FormLabel>
                    <Select
                      value={formData.year}
                      onChange={(e) => handlePeriodChange('year', e.target.value)}
                      bg="#0f1115"
                      color="#e5e7eb"
                      border="1px solid"
                      borderColor="#2d3139"
                      borderRadius="md"
                      _hover={{ borderColor: "#3d424d" }}
                      _focus={{
                        borderColor: "#60a5fa",
                        boxShadow: "0 0 0 1px #60a5fa"
                      }}
                      isDisabled={isLoadingMonthData}
                    >
                      {Array.from(
                        { length: 10 },
                        (_, i) => currentDate.getFullYear() - 2 + i
                      ).map((year) => (
                        <option key={year} value={year} style={{ backgroundColor: '#0f1115' }}>
                          {year}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="#9ca3af" fontSize="sm" mb={2}>Month</FormLabel>
                    <Select
                      value={formData.month}
                      onChange={(e) => handlePeriodChange('month', e.target.value)}
                      bg="#0f1115"
                      color="#e5e7eb"
                      border="1px solid"
                      borderColor="#2d3139"
                      borderRadius="md"
                      _hover={{ borderColor: "#3d424d" }}
                      _focus={{
                        borderColor: "#60a5fa",
                        boxShadow: "0 0 0 1px #60a5fa"
                      }}
                      isDisabled={isLoadingMonthData}
                    >
                      {monthNames.map((month, index) => (
                        <option key={index + 1} value={index + 1} style={{ backgroundColor: '#0f1115' }}>
                          {month}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <FormLabel color="#9ca3af" fontSize="sm" mb={2}>Selected Period</FormLabel>
                    <Box
                      bg="#0f1115"
                      p={3}
                      borderRadius="md"
                      border="1px solid"
                      borderColor="#2d3139"
                      h="40px"
                      display="flex"
                      alignItems="center"
                    >
                      <Text color="#e5e7eb" fontSize="sm" fontWeight="500">
                        {monthNames[formData.month - 1]} {formData.year}
                      </Text>
                      {isLoadingMonthData && (
                        <Text color="#60a5fa" fontSize="xs" ml={2}>Loading...</Text>
                      )}
                    </Box>
                  </Box>
                </SimpleGrid>
              </Box>

              <Divider borderColor="#2d3139" />

              {/* Weekly Data Table */}
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={FiTrendingUp} boxSize={5} color="#6b7280" mr={2} />
                  <Heading size="md" color="#e5e7eb" fontWeight="600">
                    Weekly Breakdown
                  </Heading>
                </Flex>

                <Box
                  bg="#0f1115"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="#2d3139"
                  overflow="hidden"
                >
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr bg="#1a1d23">
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" textTransform="none">
                            Week
                          </Th>
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" isNumeric textTransform="none">
                            Targeted Qty
                          </Th>
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" isNumeric textTransform="none">
                            Achieved Qty
                          </Th>
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" isNumeric textTransform="none">
                            Consumed Hours
                          </Th>
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" isNumeric textTransform="none">
                            Efficiency
                          </Th>
                          <Th color="#9ca3af" fontSize="xs" fontWeight="600" py={3} borderColor="#2d3139" isNumeric textTransform="none">
                            Expected Hours
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {formData.weeklyData.map((week, index) => {
                          const targeted = safeParseNumber(week.targetedQty);
                          const achieved = safeParseNumber(week.achievedQty);
                          const consumed = safeParseNumber(week.consumedManhours);

                          const efficiency = targeted > 0
                            ? ((achieved / targeted) * 100).toFixed(1)
                            : 0;

                          const expectedManhours = achieved * (taskDetails?.productivity || 0);
                          const manhourVariance = consumed - expectedManhours;

                          return (
                            <Tr
                              key={`week-${index}`}
                              _hover={{ bg: "#1a1d23" }}
                              transition="background 0.2s"
                            >
                              <Td color="#e5e7eb" py={3} borderColor="#2d3139">
                                <Flex align="center">
                                  <Box
                                    bg="#374151"
                                    color="#e5e7eb"
                                    w={6}
                                    h={6}
                                    borderRadius="md"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mr={2}
                                    fontSize="xs"
                                    fontWeight="600"
                                  >
                                    {week.week}
                                  </Box>
                                  <Text fontSize="sm">Week {week.week}</Text>
                                </Flex>
                              </Td>

                              <Td py={3} borderColor="#2d3139">
                                <Input
                                  type="text"
                                  value={week.targetedQty}
                                  onChange={(e) =>
                                    handleWeeklyDataChange(index, "targetedQty", e.target.value)
                                  }
                                  placeholder="0.00"
                                  bg="#1a1d23"
                                  color="#e5e7eb"
                                  border="1px solid"
                                  borderColor="#2d3139"
                                  borderRadius="md"
                                  fontSize="sm"
                                  textAlign="right"
                                  _hover={{ borderColor: "#3d424d" }}
                                  _focus={{
                                    borderColor: "#60a5fa",
                                    boxShadow: "0 0 0 1px #60a5fa"
                                  }}
                                  _placeholder={{ color: "#4b5563" }}
                                />
                              </Td>

                              <Td py={3} borderColor="#2d3139">
                                <Input
                                  type="text"
                                  value={week.achievedQty}
                                  onChange={(e) =>
                                    handleWeeklyDataChange(index, "achievedQty", e.target.value)
                                  }
                                  placeholder="0.00"
                                  bg="#1a1d23"
                                  color="#e5e7eb"
                                  border="1px solid"
                                  borderColor="#2d3139"
                                  borderRadius="md"
                                  fontSize="sm"
                                  textAlign="right"
                                  _hover={{ borderColor: "#3d424d" }}
                                  _focus={{
                                    borderColor: "#10b981",
                                    boxShadow: "0 0 0 1px #10b981"
                                  }}
                                  _placeholder={{ color: "#4b5563" }}
                                />
                              </Td>

                              <Td py={3} borderColor="#2d3139">
                                <Input
                                  type="text"
                                  value={week.consumedManhours}
                                  onChange={(e) =>
                                    handleWeeklyDataChange(index, "consumedManhours", e.target.value)
                                  }
                                  placeholder="0.00"
                                  bg="#1a1d23"
                                  color="#e5e7eb"
                                  border="1px solid"
                                  borderColor="#2d3139"
                                  borderRadius="md"
                                  fontSize="sm"
                                  textAlign="right"
                                  _hover={{ borderColor: "#3d424d" }}
                                  _focus={{
                                    borderColor: "#8b5cf6",
                                    boxShadow: "0 0 0 1px #8b5cf6"
                                  }}
                                  _placeholder={{ color: "#4b5563" }}
                                />
                              </Td>

                              <Td py={3} borderColor="#2d3139">
                                <Badge
                                  colorScheme={
                                    efficiency >= 100 ? "green" :
                                      efficiency >= 80 ? "yellow" : "red"
                                  }
                                  variant="subtle"
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                  fontSize="xs"
                                  fontWeight="600"
                                >
                                  {efficiency}%
                                </Badge>
                              </Td>

                              <Td isNumeric py={3} borderColor="#2d3139">
                                <VStack spacing={1} align="end">
                                  <Text color="#e5e7eb" fontSize="sm">
                                    {expectedManhours.toFixed(2)}
                                  </Text>
                                  {manhourVariance !== 0 && (
                                    <Badge
                                      colorScheme={manhourVariance > 0 ? "red" : "green"}
                                      variant="subtle"
                                      fontSize="xs"
                                      px={2}
                                    >
                                      {manhourVariance > 0 ? "+" : ""}
                                      {manhourVariance.toFixed(2)}
                                    </Badge>
                                  )}
                                </VStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              </Box>

              <Divider borderColor="#2d3139" />

              {/* Monthly Summary */}
              <Box>
                <Flex align="center" mb={4}>
                  <Icon as={FiActivity} boxSize={5} color="#6b7280" mr={2} />
                  <Heading size="md" color="#e5e7eb" fontWeight="600">Monthly Summary</Heading>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
                  {/* Target Card */}
                  <Card
                    bg="#0f1115"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#2d3139"
                  >
                    <CardBody p={4}>
                      <Stat>
                        <VStack spacing={2}>
                          <Icon as={FiTarget} boxSize={5} color="#9ca3af" />
                          <StatLabel fontSize="xs" color="#6b7280">Target</StatLabel>
                          <StatNumber fontSize="2xl" color="#e5e7eb" fontWeight="700">
                            {totals.totalTargeted.toFixed(0)}
                          </StatNumber>
                          <Text fontSize="xs" color="#6b7280">units</Text>
                        </VStack>
                      </Stat>
                    </CardBody>
                  </Card>

                  {/* Achieved Card */}
                  <Card
                    bg="#0f1115"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#2d3139"
                  >
                    <CardBody p={4}>
                      <Stat>
                        <VStack spacing={2}>
                          <Icon as={FiCheckCircle} boxSize={5} color="#10b981" />
                          <StatLabel fontSize="xs" color="#6b7280">Achieved</StatLabel>
                          <StatNumber fontSize="2xl" color="#10b981" fontWeight="700">
                            {totals.totalAchieved.toFixed(0)}
                          </StatNumber>
                          <Text fontSize="xs" color="#6b7280">units</Text>
                        </VStack>
                      </Stat>
                    </CardBody>
                  </Card>

                  {/* Consumed Hours Card */}
                  <Card
                    bg="#0f1115"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#2d3139"
                  >
                    <CardBody p={4}>
                      <Stat>
                        <VStack spacing={2}>
                          <Icon as={FiClock} boxSize={5} color="#8b5cf6" />
                          <StatLabel fontSize="xs" color="#6b7280">Consumed Hrs</StatLabel>
                          <StatNumber fontSize="2xl" color="#8b5cf6" fontWeight="700">
                            {totals.totalConsumed.toFixed(0)}
                          </StatNumber>
                          <Text fontSize="xs" color="#6b7280">hrs</Text>
                        </VStack>
                      </Stat>
                    </CardBody>
                  </Card>

                  {/* Productivity Efficiency Card */}
                  <Card
                    bg="#0f1115"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#2d3139"
                  >
                    <CardBody p={4}>
                      <Stat>
                        <VStack spacing={2}>
                          <Icon as={FiActivity} boxSize={5} color="#fb923c" />
                          <StatLabel fontSize="xs" color="#6b7280">Productivity Efficiency</StatLabel>
                          <StatNumber
                            fontSize="2xl"
                            color={productivityEfficiency >= 100 ? "#10b981" : "#fb923c"}
                            fontWeight="700"
                          >
                            {isNaN(productivityEfficiency) ? "0" : productivityEfficiency.toFixed(1)}%
                          </StatNumber>
                          <Progress
                            value={isNaN(productivityEfficiency) ? 0 : productivityEfficiency}
                            colorScheme={productivityEfficiency >= 100 ? "green" : productivityEfficiency >= 80 ? "yellow" : "orange"}
                            size="sm"
                            w="full"
                            bg="#1a1d23"
                            borderRadius="full"
                          />
                        </VStack>
                      </Stat>
                    </CardBody>
                  </Card>

                  {/* Progress Efficiency Card */}
                  <Card
                    bg="#0f1115"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="#2d3139"
                  >
                    <CardBody p={4}>
                      <Stat>
                        <VStack spacing={2}>
                          <Icon as={FiTrendingUp} boxSize={5} color="#60a5fa" />
                          <StatLabel fontSize="xs" color="#6b7280">Progress Efficiency</StatLabel>
                          <StatNumber
                            fontSize="2xl"
                            color={progressEfficiency >= 100 ? "#10b981" : progressEfficiency >= 80 ? "#fbbf24" : "#ef4444"}
                            fontWeight="700"
                          >
                            {isNaN(progressEfficiency) ? "0" : progressEfficiency.toFixed(1)}%
                          </StatNumber>
                          <Progress
                            value={isNaN(progressEfficiency) ? 0 : Math.min(progressEfficiency, 100)}
                            colorScheme={progressEfficiency >= 100 ? "green" : progressEfficiency >= 80 ? "yellow" : "red"}
                            size="sm"
                            w="full"
                            bg="#1a1d23"
                            borderRadius="full"
                          />
                        </VStack>
                      </Stat>
                    </CardBody>
                  </Card>
                </SimpleGrid>
              </Box>


              <Divider borderColor="#2d3139" />

              {/* Additional Fields */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel color="#9ca3af" fontSize="sm" mb={2}>
                    Additional Lapsed Manhours
                    <Badge colorScheme="blue" variant="subtle" ml={2} fontSize="xs">
                      Auto-calculated
                    </Badge>
                  </FormLabel>
                  <Input
                    value={formData.additionalLapsedManhours.toFixed(2)}
                    isReadOnly
                    bg="#0f1115"
                    color="#6b7280"
                    border="1px solid"
                    borderColor="#2d3139"
                    borderRadius="md"
                    _focus={{ boxShadow: "none" }}
                    cursor="not-allowed"
                  />
                  <Text color="#6b7280" fontSize="xs" mt={2}>
                    Calculated from manhour variance (positive values only)
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel color="#9ca3af" fontSize="sm" mb={2}>
                    Justification / Remarks
                  </FormLabel>
                  <Textarea
                    value={formData.justification}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        justification: e.target.value,
                      }))
                    }
                    placeholder="Explain variances, delays, or issues..."
                    maxLength={500}
                    bg="#0f1115"
                    color="#e5e7eb"
                    border="1px solid"
                    borderColor="#2d3139"
                    borderRadius="md"
                    _placeholder={{ color: "#4b5563" }}
                    _hover={{ borderColor: "#3d424d" }}
                    _focus={{
                      borderColor: "#60a5fa",
                      boxShadow: "0 0 0 1px #60a5fa"
                    }}
                    rows={4}
                    fontSize="sm"
                    resize="vertical"
                  />
                  <Text color="#6b7280" fontSize="xs" mt={2}>
                    {formData.justification.length}/500 characters
                  </Text>
                </FormControl>
              </SimpleGrid>

              {/* Validation Alert */}
              {totals.totalTargeted === 0 && (
                <Alert
                  status="warning"
                  borderRadius="md"
                  bg="rgba(251, 146, 60, 0.1)"
                  border="1px solid"
                  borderColor="rgba(251, 146, 60, 0.3)"
                >
                  <AlertIcon color="#fb923c" />
                  <AlertDescription color="#fbbf24" fontSize="sm">
                    Please enter targeted quantities to proceed.
                  </AlertDescription>
                </Alert>
              )}

              {/* Action Buttons */}
              <Flex justify="flex-end" gap={3} pt={2}>
                <Button
                  variant="outline"
                  onClick={onCancel}
                  color="#9ca3af"
                  borderColor="#2d3139"
                  _hover={{
                    bg: "#1a1d23",
                    borderColor: "#3d424d"
                  }}
                  size="md"
                  borderRadius="md"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  bg="#60a5fa"
                  color="white"
                  _hover={{
                    bg: "#3b82f6"
                  }}
                  _active={{
                    bg: "#2563eb"
                  }}
                  isLoading={isSubmitting}
                  loadingText="Saving..."
                  isDisabled={totals.totalTargeted === 0}
                  size="md"
                  borderRadius="md"
                  fontWeight="600"
                  leftIcon={<Icon as={FiCheckCircle} />}
                >
                  Save Progress
                </Button>
              </Flex>

            </VStack>
          </form>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default WeeklyProgressForm;
